import cn from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Icon } from '@src-common/view/components/Icon';

import s from './Pagination.module.css';

interface IProps {
  offset: number;
  limit: number;
  total: number;
  onChangeOffset: (offset: number) => void;
  isDisabled?: boolean;
}

interface IPaginationButtonProps {
  current: number;
  total: number;
  offset: number;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isDisabled?: boolean;
}

const PaginationButton = ({
  onClick,
  offset,
  current,
  total,
  isDisabled,
}: IPaginationButtonProps) => {
  const targetPage = current + offset;
  const isBack = offset < 0;
  const isReachedLimit = isBack ? targetPage < 1 : current === total;

  return (
    <button
      disabled={isDisabled || isReachedLimit}
      className={cn(s.button, isBack ? s.back : s.next)}
      onClick={isDisabled ? undefined : onClick}
      value={targetPage}
    >
      {isBack && <Icon icon="arrow-back" />}
      {!isReachedLimit && (
        <FormattedMessage defaultMessage="page" id="tvRjpW" description="pagination" />
      )}{' '}
      {!isReachedLimit && targetPage}
      {!isBack && <Icon icon="arrow-next" />}
    </button>
  );
};

const Pagination = ({ offset, limit, total, onChangeOffset, isDisabled }: IProps) => {
  const page = Math.floor(offset / limit) + 1;
  const totalPage = Math.floor(total / limit) + 1;

  const handleChange = useCallback(
    (event: React.SyntheticEvent<HTMLButtonElement>) => {
      onChangeOffset((Number(event.currentTarget.value) - 1) * limit);
    },
    [limit, onChangeOffset],
  );

  return (
    <div className={s.pagination}>
      <PaginationButton
        onClick={handleChange}
        current={page}
        total={totalPage}
        offset={-1}
        isDisabled={isDisabled}
      />
      <div className={s.center}>
        <FormattedMessage
          defaultMessage="{page} of {totalPage}"
          id="+TVp/2"
          description="pagination"
          values={{ page, totalPage }}
        />
      </div>
      <PaginationButton
        onClick={handleChange}
        current={page}
        total={totalPage}
        offset={+1}
        isDisabled={isDisabled}
      />
    </div>
  );
};

export { Pagination };
